import { useId, useEffect, useState } from "react";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import formatNumber from "../../services/formatNumber";

export default function AppCharts({
  height,
  data,
  color,
  tickColor,
  suffix,
  prefix,
  isLoading = false,
}: {
  width?: number;
  height?: number;
  data?: any[];
  color?: string;
  tickColor?: string;
  suffix?: string;
  prefix?: string;
  isLoading?: boolean;
}) {
  const gradientId = useId();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (data && data.length > 0 && !isLoading) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [data, isLoading]);

  if (isLoading || !data || data.length === 0) {
    return (
      <div 
        style={{ 
          height: height || 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0.6
        }}
      >
        <div style={{
          width: '40px',
          height: '40px',
          border: `3px solid ${color || '#5000a1'}`,
          borderTop: '3px solid transparent',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <div 
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
      }}
    >
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart
          data={data}
          margin={{ top: 0, right: 16, left: -10, bottom: 0 }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.8} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="name"
            fontSize={10}
            stroke={tickColor}
            tick={{ fill: tickColor }}
          />
          <YAxis
            stroke={tickColor}
            tickFormatter={(value: number) => formatNumber(value)}
            fontSize={12}
            tick={{ fill: tickColor }}
          />
          <Tooltip
            formatter={(value: number) => `${prefix ?? ''}${formatNumber(value)}${suffix ?? ''}`}
            contentStyle={{
              backgroundColor: "#ffffff",
              border: `1px solid ${color}`,
              borderRadius: "6px",
              fontSize: "12px",
              padding: "6px 10px",
            }}
            labelStyle={{
              fontSize: "12px",
              color: "#000000",
              marginBottom: "0px",
            }}
            itemStyle={{
              fontWeight: "bold",
              fontSize: "16px",
              margin: 0,
            }}
          />
          <Area
            type="monotone"
            dataKey="pv"
            stroke={color}
            fillOpacity={1}
            fill={`url(#${gradientId})`}
            animationDuration={800}
            animationEasing="ease-out"
            animationBegin={200}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
